// React hook that owns the chat state, POSTs to /api/agent/chat, and
// aggregates the streamed SSE events into { turns, pendingActions }
// the UI can render directly.
//
// Stateless w.r.t. the SERVER — the server holds no chat history. The
// client-side history is persisted to localStorage per brand so the
// operator's chat survives a page reload but stays scoped to whatever
// brand they had selected.
//
// EXTERNAL SIDE-EFFECTS:
//   - localStorage read/write, keyed by `agent-chat:v1:<brandId>`.
//   - registers this hook as the live chat writer so the shell panel
//     can see the claim and refuse to mount a second one.
//   - fetch() to `/api/agent/chat`, auth via apiFetch's Authorization
//     header injection. We can't use apiFetch directly because it
//     returns a full Response and we need to stream — but we mirror
//     its header semantics so cross-tenant / logged-out states still
//     redirect the same way.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useRegisterAgentChatWriter } from './agentChatWriters';
import { getPlanBriefing } from './briefing';
import { applyCatalogImagePlan } from './lifestylePlan';
import { readAgentEvents } from './parseSSE';
import type {
  AgentEvent, AssistantTurn, ChatAttachment, ChatAttachmentPreview, ChatContext,
  ChatTurn, Message, PendingAction, ToolCall, ToolCallRecord, WorkflowProgressEvent
} from './types';

// ── Persistence ────────────────────────────────────────────────────

const STORAGE_PREFIX = 'agent-chat:v1:';
const STORAGE_KEY_FALLBACK = STORAGE_PREFIX + 'default';

// One chat conversation per brand. Switching brands loads a different
// history; clearing history in one brand doesn't touch the other.
function storageKey(brandId: string | null | undefined): string {
  return brandId ? STORAGE_PREFIX + brandId : STORAGE_KEY_FALLBACK;
}

function historyFor(brandId: string | null | undefined, includeBriefing: boolean): { turns: ChatTurn[]; messages: Message[] } {
  const history = loadHistory(brandId);
  if (includeBriefing) return history;
  return { turns: history.turns.filter((turn) => turn.kind !== 'briefing'), messages: history.messages };
}

function loadHistory(brandId: string | null | undefined): { turns: ChatTurn[]; messages: Message[] } {
  try {
    const raw = localStorage.getItem(storageKey(brandId));
    if (!raw) return { turns: [], messages: [] };
    const parsed = JSON.parse(raw);
    if (parsed && Array.isArray(parsed.turns) && Array.isArray(parsed.messages)) {
      return parsed;
    }
    return { turns: [], messages: [] };
  } catch {
    return { turns: [], messages: [] };
  }
}

function saveHistory(brandId: string | null | undefined, turns: ChatTurn[], messages: Message[]): void {
  try {
    // Strip ephemeral attachments before persisting — base64 payloads
    // would blow the localStorage quota (5-10MB) within a few turns,
    // and attachments are server-stripped on subsequent sends anyway.
    // The user turn's text is kept; preview thumbnails are lost on
    // reload (blob: URLs don't survive).
    const safeMessages = messages.map((m) => {
      if (m.role === 'user' && (m as any).attachments) {
        const { attachments: _drop, ...rest } = m as any;
        return rest;
      }
      return m;
    });
    const safeTurns = turns.map((t) => {
      if (t.kind === 'user' && (t as any).previews) {
        const { previews: _drop, ...rest } = t as any;
        return rest;
      }
      return t;
    });
    localStorage.setItem(storageKey(brandId), JSON.stringify({ turns: safeTurns, messages: safeMessages }));
  } catch { /* quota exceeded → skip; not fatal */ }
}

// ── Auth headers ───────────────────────────────────────────────────

function authHeaders(): Record<string, string> {
  const h: Record<string, string> = {};
  const token = localStorage.getItem('token');
  if (token) h['Authorization'] = `Bearer ${token}`;
  const brandId = localStorage.getItem('brand_id');
  if (brandId) h['X-Brand-Id'] = brandId;
  const advertiserId = localStorage.getItem('advertiser_id');
  if (advertiserId) h['X-Advertiser-Id'] = advertiserId;
  return h;
}

// ── Random ID (React key / message id) ─────────────────────────────

function randomId(): string {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

// ── Public shape ───────────────────────────────────────────────────

export type UseAgentChatOptions = {
  brandId?: string | null;
  context?: ChatContext;
  // When true, an empty chat loads GET /brands/:id/briefing and shows
  // it as the first message. The briefing is not sent to /api/agent/chat.
  briefing?: boolean;
};

export type UseAgentChatResult = {
  turns:            ChatTurn[];
  pendingActions:   PendingAction[];
  sending:          boolean;
  streaming:        boolean;
  error:            string | null;
  briefingLoading:  boolean;
  send:             (text: string, attachments?: ChatAttachment[], previews?: ChatAttachmentPreview[]) => Promise<void>;
  confirm:          (toolCallIds: string[], phrases?: Record<string, string>) => Promise<void>;
  cancel:           (toolCallIds: string[]) => void;         // client-side dismiss; no re-post
  abort:            () => void;                              // cancel in-flight stream
  clear:            () => void;                              // wipe history
};

export function useAgentChat(opts: UseAgentChatOptions = {}): UseAgentChatResult {
  useRegisterAgentChatWriter();
  const brandId = opts.brandId || null;
  const briefing = opts.briefing === true;

  // Load persisted state on brand change. The reset happens during
  // render, before the save effect, so the previous brand's turns are
  // never written or sent under the new brand id.
  const [chatBrand, setChatBrand] = useState(brandId);
  const [turns, setTurns]       = useState<ChatTurn[]>(() => historyFor(brandId, briefing).turns);
  const [messages, setMessages] = useState<Message[]>(() => historyFor(brandId, briefing).messages);
  const [sending, setSending]   = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [error, setError]       = useState<string | null>(null);
  const [briefingLoading, setBriefingLoading] = useState(() => {
    if (!briefing || !brandId) return false;
    const stored = historyFor(brandId, true).turns;
    return !stored.some((turn) => turn.kind === 'user' || turn.kind === 'assistant');
  });
  const [briefingEpoch, setBriefingEpoch] = useState(0);
  const [pendingActions, setPendingActions] = useState<PendingAction[]>([]);
  const abortRef = useRef<AbortController | null>(null);
  const brandRef = useRef(brandId);
  brandRef.current = brandId;

  if (chatBrand !== brandId) {
    // Drop the previous brand's stream before this render saves state.
    abortRef.current?.abort();
    abortRef.current = null;
    const history = historyFor(brandId, briefing);
    const hasConversation = history.turns.some((turn) => turn.kind === 'user' || turn.kind === 'assistant');
    setChatBrand(brandId);
    setTurns(history.turns);
    setMessages(history.messages);
    setPendingActions([]);
    setError(null);
    setSending(false);
    setStreaming(false);
    setBriefingLoading(briefing && Boolean(brandId) && !hasConversation);
  }

  useEffect(() => { saveHistory(brandId, turns, messages); }, [brandId, turns, messages]);

  // Brand changes reload that brand's chat. An empty chat, when the
  // flag is on, opens on the stored plan briefing. The briefing is
  // only a turn: it is never added to the messages posted to the model.
  // A conversation that already has a user or assistant turn is left
  // alone. One effect owns the turn reset so a later history write
  // cannot wipe the briefing.
  useEffect(() => {
    const history = historyFor(brandId, briefing);
    setMessages(history.messages);
    setPendingActions([]);
    setError(null);
    const hasConversation = history.turns.some((turn) => turn.kind === 'user' || turn.kind === 'assistant');
    if (!briefing || !brandId || hasConversation) {
      setTurns(history.turns);
      setBriefingLoading(false);
      return;
    }
    let cancelled = false;
    setBriefingLoading(true);
    void getPlanBriefing(brandId).then((result) => {
      if (cancelled) return;
      setBriefingLoading(false);
      const next = result.briefing;
      setTurns((prev) => {
        if (prev.some((turn) => turn.kind === 'user' || turn.kind === 'assistant')) return prev;
        return next ? [{ kind: 'briefing', id: 'plan-briefing', briefing: next }] : prev.filter((turn) => turn.kind !== 'briefing');
      });
    });
    return () => {
      cancelled = true;
      setBriefingLoading(false);
    };
  }, [brandId, briefing, briefingEpoch]);

  // ── Core send loop ─────────────────────────────────────────────

  const runChat = useCallback(async (nextMessages: Message[], confirmations: string[] = [], phrases: Record<string, string> = {}) => {
    setSending(true);
    setStreaming(true);
    setError(null);

    // Create an in-progress assistant turn we mutate as events arrive.
    // Each iteration on the server bumps `iteration` — for the client
    // we treat the whole streamed response as ONE assistant turn (with
    // multiple tool calls). Multi-iteration on the wire flattens into
    // one visual "assistant turn" the operator reads top-to-bottom.
    const assistantId = randomId();
    const startedAt = Date.now();
    const assistantTurn: AssistantTurn = {
      id:        assistantId,
      content:   '',
      thinking:  '',
      toolCalls: [],
      streaming: true,
      startedAt,
      lastEventAt: startedAt,
      iteration: 0
    };
    setTurns((prev) => [...prev, { kind: 'assistant', id: assistantId, turn: assistantTurn }]);

    // Emit a state update every ~1s while streaming so the "Thinking…"
    // affordance can render an elapsed-time counter without waiting on
    // an event to trigger a re-render. Cleared in `finally`.
    const heartbeat = setInterval(() => {
      if (!assistantTurn.streaming) return;
      setTurns((prev) => prev.map((t) => (t.kind === 'assistant' && t.id === assistantId
        ? { ...t, turn: { ...assistantTurn } }
        : t
      )));
    }, 1000);

    const abort = new AbortController();
    abortRef.current = abort;
    const forBrand = brandId;
    const sameBrand = () => brandRef.current === forBrand;

    // Buffered SSE event accumulators. React setState in event handler is
    // fine but we also mutate the local turn state so tool-call rows
    // build up in order.
    const upsertToolCall = (
      toolCallId: string,
      patch: Partial<ToolCallRecord>
    ) => {
      const idx = assistantTurn.toolCalls.findIndex((t) => t.toolCallId === toolCallId);
      if (idx < 0) {
        assistantTurn.toolCalls.push({
          toolCallId,
          toolName: patch.toolName || 'unknown',
          status:   'started',
          progress: [],
          ...patch
        });
      } else {
        const existing = assistantTurn.toolCalls[idx];
        assistantTurn.toolCalls[idx] = { ...existing, ...patch };
      }
      // Trigger React re-render — replace the turns entry with a new
      // object reference (React doesn't diff deeply).
      setTurns((prev) => prev.map((t) => (t.kind === 'assistant' && t.id === assistantId
        ? { ...t, turn: { ...assistantTurn, toolCalls: [...assistantTurn.toolCalls] } }
        : t
      )));
    };

    // The updated messages array we'll assemble as we go. Server
    // returns the "assembled" assistant + tool messages via the SSE
    // stream — we build the same shape locally by watching the events.
    const workingMessages: Message[] = [...nextMessages];
    const collectedToolCalls: ToolCall[] = [];
    const collectedToolResults: Message[] = [];

    try {
      const response = await fetch('/api/agent/chat', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          messages: applyCatalogImagePlan(nextMessages, opts.context),
          context:  opts.context || {},
          confirmations,
          explicitConfirmations: phrases
        }),
        signal: abort.signal
      });

      if (!response.ok) {
        let msg = `agent responded ${response.status}`;
        try {
          const body = await response.json();
          if (body?.error) msg = body.error;
        } catch { /* not JSON */ }
        throw new Error(msg);
      }

      let pendingList: PendingAction[] = [];

      for await (const evt of readAgentEvents(response)) {
        if (abort.signal.aborted) break;
        handleEvent(evt);
      }

      setPendingActions(pendingList);

      function handleEvent(evt: AgentEvent): void {
        assistantTurn.lastEventAt = Date.now();
        switch (evt.type) {
          case 'iteration':
            assistantTurn.iteration = evt.n + 1;
            setTurns((prev) => prev.map((t) => (t.kind === 'assistant' && t.id === assistantId
              ? { ...t, turn: { ...assistantTurn } }
              : t
            )));
            break;
          case 'assistant-delta':
            assistantTurn.content += evt.text;
            setTurns((prev) => prev.map((t) => (t.kind === 'assistant' && t.id === assistantId
              ? { ...t, turn: { ...assistantTurn } }
              : t
            )));
            break;
          case 'assistant-thinking-delta':
            assistantTurn.thinking += evt.text;
            setTurns((prev) => prev.map((t) => (t.kind === 'assistant' && t.id === assistantId
              ? { ...t, turn: { ...assistantTurn } }
              : t
            )));
            break;
          case 'tool-use-start':
            upsertToolCall(evt.toolCallId, {
              toolCallId: evt.toolCallId,
              toolName:   evt.toolName,
              status:     'started'
            });
            break;
          case 'tool-use-complete':
            upsertToolCall(evt.toolCallId, {
              toolName: evt.toolName,
              args:     evt.args,
              status:   'complete'
            });
            collectedToolCalls.push({
              id:       evt.toolCallId,
              type:     'function',
              function: { name: evt.toolName, arguments: JSON.stringify(evt.args) }
            });
            break;
          case 'tool-result':
            upsertToolCall(evt.toolCallId, {
              result: evt.result,
              status: 'result'
            });
            collectedToolResults.push({
              role:         'tool',
              tool_call_id: evt.toolCallId,
              content:      JSON.stringify(evt.result)
            });
            break;
          case 'proposed-action':
            upsertToolCall(evt.toolCallId, {
              toolName: evt.toolName,
              args:     evt.args,
              tier:     evt.tier,
              status:   'proposed'
            });
            pendingList.push({
              toolCallId: evt.toolCallId,
              toolName:   evt.toolName,
              args:       evt.args,
              tier:       evt.tier
            });
            break;
          case 'plan-proposed':
            upsertToolCall(evt.toolCallId, {
              toolName: evt.toolName,
              plan:     evt.plan,
              status:   'planned'
            });
            pendingList.push({
              toolCallId: evt.toolCallId,
              toolName:   evt.toolName,
              args:       {},   // workflows carry args in the plan itself
              tier:       4,
              plan:       evt.plan
            });
            break;
          case 'workflow-progress':
            {
              const idx = assistantTurn.toolCalls.findIndex((t) => t.toolCallId === evt.toolCallId);
              if (idx >= 0) {
                const existing = assistantTurn.toolCalls[idx];
                const nextProgress = [...(existing.progress || []), evt as WorkflowProgressEvent];
                upsertToolCall(evt.toolCallId, { progress: nextProgress });
              }
            }
            break;
          case 'spend-guard-block':
            upsertToolCall(evt.toolCallId, {
              status: 'blocked',
              block:  evt
            });
            break;
          case 'tier3-phrase-block':
            upsertToolCall(evt.toolCallId, {
              status: 'blocked',
              block:  evt
            });
            // Re-surface as a pending action if not already there,
            // carrying the required phrase for the re-confirm UI.
            {
              const already = pendingList.find((p) => p.toolCallId === evt.toolCallId);
              if (already) {
                already.requiredPhrase = evt.required;
              } else {
                pendingList.push({
                  toolCallId: evt.toolCallId,
                  toolName:   evt.toolName,
                  args:       {},
                  tier:       3,
                  requiredPhrase: evt.required
                });
              }
            }
            break;
          case 'done':
            assistantTurn.streaming = false;
            setTurns((prev) => prev.map((t) => (t.kind === 'assistant' && t.id === assistantId
              ? { ...t, turn: { ...assistantTurn } }
              : t
            )));
            break;
          case 'error':
            assistantTurn.streaming = false;
            setError(evt.error);
            setTurns((prev) => prev.map((t) => (t.kind === 'assistant' && t.id === assistantId
              ? { ...t, turn: { ...assistantTurn } }
              : t
            )));
            break;
        }
      }

      // Fold the assistant + tool messages into the message history so
      // next turn (confirmation) has them.
      const finalAssistantMsg: Message = collectedToolCalls.length
        ? { role: 'assistant', content: assistantTurn.content || null, tool_calls: collectedToolCalls }
        : { role: 'assistant', content: assistantTurn.content || '' };
      if (!sameBrand()) return;
      const nextMessagesFinal = [...workingMessages, finalAssistantMsg, ...collectedToolResults];
      setMessages(nextMessagesFinal);
    } catch (err) {
      if ((err as Error).name === 'AbortError') return;
      if (!sameBrand()) return;
      const msg = (err as Error).message || 'chat failed';
      setError(msg);
      assistantTurn.streaming = false;
      setTurns((prev) => prev.map((t) => (t.kind === 'assistant' && t.id === assistantId
        ? { ...t, turn: { ...assistantTurn } }
        : t
      )));
    } finally {
      clearInterval(heartbeat);
      if (abortRef.current === abort) abortRef.current = null;
      if (!sameBrand()) return;
      // Robust cleanup: if the stream ended without a terminal 'done' or
      // 'error' event (proxy truncation, connection dropped after last
      // chunk, race between server res.write and res.end), the turn
      // would otherwise be stuck at streaming:true forever showing
      // "Thinking…". Force it closed. No error surfaced because the
      // content we DID accumulate is still valid to display.
      if (assistantTurn.streaming) {
        assistantTurn.streaming = false;
        setTurns((prev) => prev.map((t) => (t.kind === 'assistant' && t.id === assistantId
          ? { ...t, turn: { ...assistantTurn } }
          : t
        )));
      }
      setSending(false);
      setStreaming(false);
    }
  }, [opts.context, brandId]);

  // ── Public actions ────────────────────────────────────────────

  const send = useCallback(async (text: string, attachments: ChatAttachment[] = [], previews: ChatAttachmentPreview[] = []) => {
    const trimmed = text.trim();
    // Allow sending with only attachments + empty text (e.g. "describe
    // this image"). An empty send with zero attachments stays no-op.
    if ((!trimmed && attachments.length === 0) || sending) return;
    const userTurn: ChatTurn = {
      kind: 'user',
      id: randomId(),
      content: trimmed,
      ...(previews.length ? { previews } : {}),
    };
    setTurns((prev) => [...prev, userTurn]);
    setPendingActions([]);
    // Strip attachments[] from any PRIOR user message in state before
    // sending — ephemeral enforcement, mirrors the server's own strip.
    // Saves memory + bandwidth on multi-turn chats.
    const historyStripped: Message[] = messages.map((m) => {
      if (m.role === 'user' && (m as any).attachments) {
        const { attachments: _drop, ...rest } = m as any;
        return rest;
      }
      return m;
    });
    const newUserMsg: Message = attachments.length > 0
      ? { role: 'user', content: trimmed, attachments }
      : { role: 'user', content: trimmed };
    const nextMessages: Message[] = [...historyStripped, newUserMsg];
    await runChat(nextMessages);
  }, [messages, sending, runChat]);

  const confirm = useCallback(async (toolCallIds: string[], phrases: Record<string, string> = {}) => {
    if (!toolCallIds.length) return;
    setPendingActions((prev) => prev.filter((p) => !toolCallIds.includes(p.toolCallId)));
    await runChat(messages, toolCallIds, phrases);
  }, [messages, runChat]);

  const cancel = useCallback((toolCallIds: string[]) => {
    setPendingActions((prev) => prev.filter((p) => !toolCallIds.includes(p.toolCallId)));
  }, []);

  const abort = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
  }, []);

  const clear = useCallback(() => {
    setTurns([]);
    setMessages([]);
    setPendingActions([]);
    setError(null);
    try { localStorage.removeItem(storageKey(brandId)); } catch { /* noop */ }
    setBriefingEpoch((epoch) => epoch + 1);
  }, [brandId]);

  return { turns, pendingActions, sending, streaming, error, briefingLoading, send, confirm, cancel, abort, clear };
}
